import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, FormControl, InputLabel, Select,
  MenuItem, IconButton, Snackbar, Alert, TextField, Divider, Chip, Checkbox, FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add, Delete, CalendarMonth, Event } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

export default function SchedulePage() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [events, setEvents] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [calendarName, setCalendarName] = useState('Home Management with Hubsicle');
  const [selectedForCal, setSelectedForCal] = useState(new Set());

  const dateStr = selectedDate.format('YYYY-MM-DD');

  useEffect(() => {
    api.getClasses().then(data => setClasses(data.filter(c => c.is_active)));
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const start = selectedDate.startOf('month').format('YYYY-MM-DD');
      const end = selectedDate.endOf('month').add(1, 'month').format('YYYY-MM-DD');
      const data = await api.getScheduledEvents(start, end);
      setEvents(data);
    } catch (err) {
      console.error(err);
    }
  }, [selectedDate]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // When upcoming events load/change, pre-select all by default
  useEffect(() => {
    const upcoming = events
      .filter(e => (e.event_date || e.eventDate) >= dayjs().format('YYYY-MM-DD'));
    setSelectedForCal(new Set(upcoming.map(e => e.id)));
  }, [events]);

  const handleSchedule = async () => {
    if (!selectedClassId || !dateStr) return;
    setSaving(true);
    try {
      await api.createScheduledEvent({
        class_id: selectedClassId,
        event_date: dateStr,
        start_time: startTime,
        end_time: endTime,
        notes,
      });
      setSnackbar({ open: true, message: 'Class scheduled!', severity: 'success' });
      setSelectedClassId('');
      setStartTime('');
      setEndTime('');
      setNotes('');
      fetchEvents();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteScheduledEvent(id);
      setSnackbar({ open: true, message: 'Event removed', severity: 'info' });
      fetchEvents();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const eventsForDate = events.filter(e => (e.event_date || e.eventDate) === dateStr);
  const upcomingEvents = events
    .filter(e => (e.event_date || e.eventDate) >= dayjs().format('YYYY-MM-DD'))
    .sort((a, b) => (a.event_date || a.eventDate || '').localeCompare(b.event_date || b.eventDate || ''));


  const allChecked = upcomingEvents.length > 0 && upcomingEvents.every(e => selectedForCal.has(e.id));
  const toggleAll = () => {
    if (allChecked) {
      setSelectedForCal(new Set());
    } else {
      setSelectedForCal(new Set(upcomingEvents.map(e => e.id)));
    }
  };
  const toggleOne = (id) => {
    setSelectedForCal(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddToCal = () => {
    const toExport = upcomingEvents.filter(e => selectedForCal.has(e.id));
    if (toExport.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one class to export', severity: 'warning' });
      return;
    }
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//ClassMonitor//Schedule//EN',
      `X-WR-CALNAME:${calendarName}`,
    ];
    for (const e of toExport) {
      const date = (e.event_date || e.eventDate || '').replace(/-/g, '');
      const dtStart = (e.start_time || e.startTime || '09:00').replace(':', '') + '00';
      const dtEnd = (e.end_time || e.endTime || '10:00').replace(':', '') + '00';
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:${e.id}@classmonitor`);
      lines.push(`DTSTART:${date}T${dtStart}`);
      lines.push(`DTEND:${date}T${dtEnd}`);
      lines.push(`SUMMARY:${e.class_name}`);
      if (e.notes) lines.push(`DESCRIPTION:${e.notes}`);
      lines.push('END:VEVENT');
    }
    lines.push('END:VCALENDAR');
    const icsContent = lines.join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    setSnackbar({ open: true, message: 'Opening calendar...', severity: 'success' });
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        <CalendarMonth sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
        Schedule Classes
      </Typography>

      {/* Schedule a new event */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>New Scheduled Class</Typography>

          <DatePicker
            label="Date"
            value={selectedDate}
            onChange={(val) => setSelectedDate(val)}
            sx={{ width: '100%', mb: 2 }}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Class</InputLabel>
            <Select
              value={selectedClassId}
              onChange={e => setSelectedClassId(e.target.value)}
              label="Select Class"
            >
              {classes.map(cls => (
                <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <Button
            fullWidth variant="contained" startIcon={<Add />}
            onClick={handleSchedule}
            disabled={!selectedClassId || saving}
            sx={{ py: 1.5 }}
          >
            {saving ? 'Scheduling...' : 'Schedule Class'}
          </Button>
        </CardContent>
      </Card>

      {/* Events on selected date */}
      {eventsForDate.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Scheduled on {dayjs(dateStr).format('dddd, MMMM D')}
          </Typography>
          {eventsForDate.map(event => (
            <Card key={event.id} sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography fontWeight={600}>{event.class_name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      {(event.start_time || event.startTime) && (
                        <Chip
                          label={`${event.start_time || event.startTime}${(event.end_time || event.endTime) ? ' - ' + (event.end_time || event.endTime) : ''}`}
                          size="small" variant="outlined"
                        />
                      )}
                      {event.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          {event.notes}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => handleDelete(event.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Upcoming events */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Upcoming Scheduled Classes</Typography>
        {upcomingEvents.length > 0 && (
          <FormControlLabel
            control={<Checkbox size="small" checked={allChecked} indeterminate={selectedForCal.size > 0 && !allChecked} onChange={toggleAll} />}
            label={<Typography variant="caption" color="text.secondary">Select all</Typography>}
            sx={{ mr: 0 }}
          />
        )}
      </Box>
      {upcomingEvents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No upcoming classes scheduled</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {upcomingEvents.map(event => (
            <Card key={event.id} variant="outlined" sx={{ borderColor: selectedForCal.has(event.id) ? '#93A3D8' : undefined }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selectedForCal.has(event.id)}
                      onChange={() => toggleOne(event.id)}
                      sx={{ p: 0.5 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{event.class_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(event.event_date || event.eventDate).format('ddd, MMM D')}
                        {(event.start_time || event.startTime) ? ` at ${event.start_time || event.startTime}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => handleDelete(event.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add to Calendar */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Add to Calendar</Typography>
      <TextField
        label="Calendar Name"
        value={calendarName}
        onChange={e => setCalendarName(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
        helperText="Name suggested to your calendar app on import"
      />
      <Button
        fullWidth variant="outlined" startIcon={<Event />}
        onClick={handleAddToCal}
        disabled={selectedForCal.size === 0}
      >
        Add to Cal ({selectedForCal.size} event{selectedForCal.size !== 1 ? 's' : ''})
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
